import { z } from 'zod';

export const GlossaryEntrySchema = z.object({
  id: z.string(),
  acronym: z.string(),
  meaning: z.string(),
  explanation: z.string(),
  category: z.enum(['academic', 'behavioral', 'administrative', 'special_education', 'general']),
  lastModified: z.string()
});

export type GlossaryEntry = z.infer<typeof GlossaryEntrySchema>;

export const initialGlossaryData: GlossaryEntry[] = [
  {
    id: '1',
    acronym: 'BIC',
    meaning: 'Behavior Intervention Consultation',
    explanation: 'A specialized, data-driven service unique to LAUSD, delivered by certified staff to help address student behaviors that don\'t require intensive support.',
    category: 'behavioral',
    lastModified: new Date().toISOString()
  },
  {
    id: '2',
    acronym: 'BID',
    meaning: 'Behavior Intervention Development',
    explanation: 'A data-driven service delivered by certified staff to provide support when student behavior is significant enough to require ongoing help.',
    category: 'behavioral',
    lastModified: new Date().toISOString()
  },
  {
    id: '3',
    acronym: 'BII',
    meaning: 'Behavior Intervention Implementation',
    explanation: 'Service delivered by certified staff, under the supervision of BID providers, to implement the Behavior Treatment Plan and support the student with tools and visuals.',
    category: 'behavioral',
    lastModified: new Date().toISOString()
  },
  {
    id: '4',
    acronym: 'BIP',
    meaning: 'Behavior Intervention Plan',
    explanation: 'Plan outlining strategies and interventions for students with disabilities exhibiting behaviors that impede learning, based on data from a Functional Behavioral Assessment.',
    category: 'behavioral',
    lastModified: new Date().toISOString()
  },
  {
    id: '5',
    acronym: 'CCSS',
    meaning: 'Common Core State Standards',
    explanation: 'Standards that outline what students should learn in school to prepare them for college and careers.',
    category: 'academic',
    lastModified: new Date().toISOString()
  },
  {
    id: '6',
    acronym: 'CDE',
    meaning: 'California Department of Education',
    explanation: 'The state education agency of California that oversees public education.',
    category: 'administrative',
    lastModified: new Date().toISOString()
  },
  {
    id: '7',
    acronym: 'CoS',
    meaning: 'Community of Schools',
    explanation: 'An administrative unit within LAUSD, similar to a local district.',
    category: 'administrative',
    lastModified: new Date().toISOString()
  },
  {
    id: '8',
    acronym: 'CoSA',
    meaning: 'Community of Schools Administrator',
    explanation: 'Administrator responsible for overseeing a Community of Schools within LAUSD.',
    category: 'administrative',
    lastModified: new Date().toISOString()
  },
  {
    id: '9',
    acronym: 'DSE',
    meaning: 'Division of Special Education',
    explanation: 'Department responsible for overseeing special education services and programs.',
    category: 'special_education',
    lastModified: new Date().toISOString()
  },
  {
    id: '10',
    acronym: 'FAPE',
    meaning: 'Free Appropriate Public Education',
    explanation: 'Legal right of all children with disabilities in the United States to receive free public education that meets their individual needs.',
    category: 'special_education',
    lastModified: new Date().toISOString()
  },
  {
    id: '11',
    acronym: 'FBA',
    meaning: 'Functional Behavior Assessment',
    explanation: 'Evidence-based assessment used to understand the reasons behind a student\'s behavior and to inform the development of a BIP.',
    category: 'behavioral',
    lastModified: new Date().toISOString()
  },
  {
    id: '12',
    acronym: 'FTE',
    meaning: 'Full-Time Equivalent',
    explanation: 'A unit that indicates the workload of one full-time employee for a given period.',
    category: 'administrative',
    lastModified: new Date().toISOString()
  },
  {
    id: '13',
    acronym: 'GATE',
    meaning: 'Gifted and Talented Education',
    explanation: 'Educational programs designed to meet the needs of gifted and talented students.',
    category: 'academic',
    lastModified: new Date().toISOString()
  },
  {
    id: '14',
    acronym: 'IBRP',
    meaning: 'Interim Behavior Response Plan',
    explanation: 'Plan outlining strategies and interventions for students exhibiting challenging behavior.',
    category: 'behavioral',
    lastModified: new Date().toISOString()
  },
  {
    id: '15',
    acronym: 'IDEIA',
    meaning: 'Individuals with Disabilities Education Improvement Act',
    explanation: 'Federal law ensuring students with disabilities receive a free and appropriate public education tailored to their individual needs.',
    category: 'special_education',
    lastModified: new Date().toISOString()
  },
  {
    id: '16',
    acronym: 'LAUSD',
    meaning: 'Los Angeles Unified School District',
    explanation: 'The largest public school system in California and the second-largest in the United States.',
    category: 'administrative',
    lastModified: new Date().toISOString()
  },
  {
    id: '17',
    acronym: 'LD',
    meaning: 'Local District',
    explanation: 'Administrative units within LAUSD, similar to school districts in other areas.',
    category: 'administrative',
    lastModified: new Date().toISOString()
  },
  {
    id: '18',
    acronym: 'MTSS',
    meaning: 'Multi-Tiered System of Supports',
    explanation: 'Framework using data to inform a tiered approach to providing academic, behavioral, and social-emotional support to students.',
    category: 'academic',
    lastModified: new Date().toISOString()
  },
  {
    id: '19',
    acronym: 'PBIS',
    meaning: 'Positive Behavior Interventions and Supports',
    explanation: 'Proactive framework designed to improve school safety and student behavior.',
    category: 'behavioral',
    lastModified: new Date().toISOString()
  },
  {
    id: '20',
    acronym: 'PBIS/RP',
    meaning: 'Positive Behavior Interventions and Supports/Restorative Practices',
    explanation: 'Framework combining PBIS with restorative practices to create positive and supportive school environments.',
    category: 'behavioral',
    lastModified: new Date().toISOString()
  },
  {
    id: '21',
    acronym: 'RP',
    meaning: 'Restorative Practices',
    explanation: 'Practices focusing on repairing harm and restoring relationships within a community when wrongdoing has occurred.',
    category: 'behavioral',
    lastModified: new Date().toISOString()
  },
  {
    id: '22',
    acronym: 'SEL',
    meaning: 'Social Emotional Learning',
    explanation: 'Process helping individuals develop self-awareness, self-management, social awareness, relationship skills, and responsible decision-making.',
    category: 'behavioral',
    lastModified: new Date().toISOString()
  },
  {
    id: '23',
    acronym: 'SSPT',
    meaning: 'Student Support and Progress Team',
    explanation: 'Team that meets to discuss and develop interventions for students needing additional support.',
    category: 'academic',
    lastModified: new Date().toISOString()
  },
  {
    id: '24',
    acronym: 'SWD',
    meaning: 'Students with Disabilities',
    explanation: 'Students who have been identified as having one or more disabilities that affect their learning.',
    category: 'special_education',
    lastModified: new Date().toISOString()
  },
  {
    id: '25',
    acronym: 'SWPBIS',
    meaning: 'School-Wide Positive Behavior Interventions and Support',
    explanation: 'A comprehensive approach to implementing PBIS across an entire school community.',
    category: 'behavioral',
    lastModified: new Date().toISOString()
  },
  {
    id: '26',
    acronym: 'TFI',
    meaning: 'Tiered Fidelity Inventory',
    explanation: 'A tool used to assess the implementation of the school\'s discipline plan and PBIS/RP framework at all three tiers.',
    category: 'behavioral',
    lastModified: new Date().toISOString()
  }
];